/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IComponent, IComponentProps, ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import React from 'react';

import { QuickOrderGridProduct } from '../quick-order-grid-product';
import { IQuickOrderGridRowCallbacks } from '../utilities';
import {
    IQuickOrderGridTableHeaderResources,
    IQuickOrderGridTableRowResources,
    QuickOrderGridTableHeaderComponent,
    QuickOrderGridTableRowComponent
} from './components';

/**
 * Quick order table data interface.
 */
interface IQuickOrderGridTableData {
    quickOrderGridProducts: QuickOrderGridProduct[];
}

/**
 * Quick order table resources.
 */
export interface IQuickOrderGridTableResources extends IQuickOrderGridTableHeaderResources, IQuickOrderGridTableRowResources {}

/**
 * Quick order table props.
 */
export interface IQuickOrderGridTableProps extends IComponentProps<IQuickOrderGridTableData> {
    context: ICoreContext;
    className: string;
    moduleId: string;
    moduleTypeName: string;

    resources: IQuickOrderGridTableResources;
    isMobileView: boolean;
    imageSettings?: IImageSettings;
    callBacks: IQuickOrderGridRowCallbacks;
}

/**
 * Quick order table component.
 */
export interface IQuickOrderGridTableComponent extends IComponent<IQuickOrderGridTableProps> {}

/**
 * The quick order table.
 * @param props -- The quick order table props.
 * @returns The quick order table element.
 */
export const QuickOrderGridTableComponent: React.FC<IQuickOrderGridTableProps> = (props: IQuickOrderGridTableProps) => {
    const [isMobile, setIsMobile] = React.useState(props.isMobileView);
    const quickOrderGridProducts = props.data.quickOrderGridProducts;

    /**
     * Update the view port.
     */
    const updateViewport = () => {
        setIsMobile(props.isMobileView);
    };

    if (MsDyn365.isBrowser) {
        window.addEventListener('resize', updateViewport);
    }

    const headerClassName = `${props.className}__header`;
    const rowClassName = `${props.className}__row`;

    return (
        <table className={props.className}>
            <QuickOrderGridTableHeaderComponent
                context={props.context}
                typeName={props.typeName}
                id={headerClassName}
                className={headerClassName}
                isMobile={isMobile}
                resources={props.resources}
                data={{}}
            />
            {quickOrderGridProducts.map((quickOrderProduct, index) => {
                const price = quickOrderProduct.productLine.fullProduct?.ProductPrice;
                const quantity = quickOrderProduct.productLine.productListLine.Quantity!;
                return (
                    <QuickOrderGridTableRowComponent
                        context={props.context}
                        typeName={props.typeName}
                        id={`${rowClassName}-${index}`}
                        key={quickOrderProduct.productLine.fullProduct?.ProductDetails.RecordId}
                        className={rowClassName}
                        resources={props.resources}
                        isMobile={isMobile}
                        price={price}
                        moduleId={props.moduleId}
                        moduleTypeName={props.moduleTypeName}
                        quantity={quantity}
                        index={index}
                        imageSettings={props.imageSettings}
                        productListLine={quickOrderProduct.productLine.productListLine}
                        callbacks={props.callBacks}
                        data={{
                            product: quickOrderProduct
                        }}
                    />
                );
            })}
        </table>
    );
};
